import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { PlanDay, PlanFrame, updateFrame } from '@/lib/contentPlanner';
import { toast } from 'sonner';
import {
  Sparkles,
  X,
  Check,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  PlayCircle,
  StopCircle,
  ChevronRight,
} from 'lucide-react';

type RowStatus = 'pending' | 'generating' | 'done' | 'skipped' | 'error';

interface Row {
  dayId: string;
  frameId: string;
  dayLabel: string;
  theme: string;
  postType: string;
  image: string;
  existingCaption: string;
  status: RowStatus;
  reason?: string; // skip / error reason
  result?: { caption: string; hashtags: string[] };
}

interface Props {
  open: boolean;
  onClose: () => void;
  days: PlanDay[];
  onUpdated: () => void; // reload data
}

// How long a caption must be to count as "already written" (skip threshold)
const SHORT_CAPTION_THRESHOLD = 30;
// Delay between gateway calls to avoid rate limits (ms)
const DELAY_BETWEEN_CALLS_MS = 1200;

const statusMeta: Record<RowStatus, { label: string; cls: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    cls: 'bg-slate-100 text-slate-500 border-slate-200',
    icon: <span className="w-2 h-2 rounded-full bg-slate-300" />,
  },
  generating: {
    label: 'Generating…',
    cls: 'bg-[#C9A961]/10 text-[#8B6F47] border-[#C9A961]/40',
    icon: <Loader2 className="w-3 h-3 animate-spin text-[#C9A961]" />,
  },
  done: {
    label: 'Updated',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    icon: <Check className="w-3 h-3 text-emerald-600" />,
  },
  skipped: {
    label: 'Skipped',
    cls: 'bg-slate-50 text-slate-500 border-slate-200',
    icon: <ChevronRight className="w-3 h-3 text-slate-400" />,
  },
  error: {
    label: 'Failed',
    cls: 'bg-red-50 text-red-700 border-red-200',
    icon: <AlertCircle className="w-3 h-3 text-red-500" />,
  },
};

function buildInitialRows(days: PlanDay[]): Row[] {
  const rows: Row[] = [];
  for (const d of days) {
    for (const f of d.frames) {
      // Decide initial status
      let status: RowStatus = 'pending';
      let reason: string | undefined;

      if (d.approval_status === 'Approved' || f.approval_status === 'Approved') {
        status = 'skipped';
        reason = 'Already approved';
      } else if (!f.image || !f.image.trim()) {
        status = 'skipped';
        reason = 'No image';
      } else if (f.caption && f.caption.trim().length >= SHORT_CAPTION_THRESHOLD) {
        status = 'skipped';
        reason = 'Caption already written';
      }

      rows.push({
        dayId: d.id,
        frameId: f.id,
        dayLabel: d.day_label || 'Untitled day',
        theme: d.theme || '',
        postType: f.post_type || 'Feed Post',
        image: f.image,
        existingCaption: f.caption || '',
        status,
        reason,
      });
    }
  }
  return rows;
}

const BulkAIGenerateModal: React.FC<Props> = ({ open, onClose, days, onUpdated }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const cancelRef = useRef(false);

  // Reset when modal opens / days change
  useEffect(() => {
    if (open) {
      setRows(buildInitialRows(days));
      setRunning(false);
      setFinished(false);
      cancelRef.current = false;
    }
  }, [open, days]);

  const eligibleCount = useMemo(
    () => rows.filter((r) => r.status === 'pending').length,
    [rows],
  );
  const skippedCount = useMemo(
    () => rows.filter((r) => r.status === 'skipped').length,
    [rows],
  );
  const doneCount = useMemo(
    () => rows.filter((r) => r.status === 'done').length,
    [rows],
  );
  const errorCount = useMemo(
    () => rows.filter((r) => r.status === 'error').length,
    [rows],
  );

  const setRow = (frameId: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.frameId === frameId ? { ...r, ...patch } : r)));
  };

  const runGenerate = async () => {
    setRunning(true);
    setFinished(false);
    cancelRef.current = false;

    const queue = rows.filter((r) => r.status === 'pending');

    let updatedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < queue.length; i++) {
      if (cancelRef.current) break;
      const row = queue[i];

      setRow(row.frameId, { status: 'generating' });

      try {
        const { data, error } = await supabase.functions.invoke('caption-suggest', {
          body: {
            theme: row.theme,
            imageUrl: row.image,
            dayLabel: row.dayLabel,
            postType: row.postType,
            existingCaption: row.existingCaption,
          },
        });
        if (error) throw new Error(error.message);
        if (!data || data.error) throw new Error(data?.error || 'No result');

        const variant = data.captions?.[0];
        const tiers = data.hashtags || {};
        const allTags: string[] = [
          ...(Array.isArray(tiers.broad) ? tiers.broad : []),
          ...(Array.isArray(tiers.mid) ? tiers.mid : []),
          ...(Array.isArray(tiers.niche) ? tiers.niche : []),
        ];

        if (!variant?.caption) throw new Error('Empty caption returned');

        // Persist to DB
        await updateFrame(row.frameId, {
          caption: variant.caption,
          hashtags: allTags,
        });

        setRow(row.frameId, {
          status: 'done',
          result: { caption: variant.caption, hashtags: allTags },
        });
        updatedCount += 1;
      } catch (e: any) {
        setRow(row.frameId, {
          status: 'error',
          reason: e?.message || 'Generation failed',
        });
        failedCount += 1;
      }

      // Small delay between calls (skip after last item)
      if (i < queue.length - 1 && !cancelRef.current) {
        await new Promise((r) => setTimeout(r, DELAY_BETWEEN_CALLS_MS));
      }
    }

    setRunning(false);
    setFinished(true);

    // Refresh parent data so UI reflects new captions
    onUpdated();

    // Summary toast
    if (updatedCount > 0 && failedCount === 0) {
      toast.success(
        `Generated ${updatedCount} caption${updatedCount === 1 ? '' : 's'} with AI`,
        {
          description: `${skippedCount} skipped (already written, approved, or no image).`,
        },
      );
    } else if (updatedCount > 0 && failedCount > 0) {
      toast.warning(
        `Updated ${updatedCount}, ${failedCount} failed`,
        { description: 'Review failed rows and retry — they may be transient gateway errors.' },
      );
    } else if (updatedCount === 0 && failedCount > 0) {
      toast.error(`Generation failed for all ${failedCount} posts`, {
        description: 'Check the gateway key or try again in a moment.',
      });
    } else if (cancelRef.current) {
      toast.message('Generation cancelled', {
        description: `${updatedCount} updated before stopping.`,
      });
    } else {
      toast.message('Nothing to update', {
        description: 'All eligible posts were already filled in.',
      });
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
  };

  if (!open) return null;

  const progressDone =
    rows.length === 0
      ? 0
      : Math.round(
          ((doneCount + errorCount + skippedCount) / rows.length) * 100,
        );

  return (
    <div className="fixed inset-0 z-[100] bg-[#0D1B2A]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-[#0D1B2A] to-[#1A3B5C]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C9A961]" />
            </div>
            <div>
              <h2 className="font-serif text-white text-lg font-light">Generate Week with AI</h2>
              <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/50">
                Auto-fill captions &amp; hashtags for every draft post
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-white/60 hover:text-white p-2 transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary bar */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="grid grid-cols-4 gap-3 mb-3">
            <Stat label="Eligible" value={eligibleCount + (running ? 0 : 0)} accent="text-[#1A3B5C]" />
            <Stat label="Skipped" value={skippedCount} accent="text-slate-500" />
            <Stat label="Updated" value={doneCount} accent="text-emerald-600" />
            <Stat label="Failed" value={errorCount} accent="text-red-600" />
          </div>
          {/* Progress bar */}
          <div className="h-1.5 w-full bg-slate-200 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1A3B5C] to-[#C9A961] transition-all duration-300"
              style={{ width: `${progressDone}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="font-sans text-[10px] tracking-wider uppercase text-slate-500">
              {finished
                ? 'Run complete'
                : running
                  ? `Processing… ${doneCount + errorCount} of ${eligibleCount + doneCount + errorCount} done`
                  : `${eligibleCount} post${eligibleCount === 1 ? '' : 's'} ready to generate`}
            </p>
            <p className="font-sans text-[10px] text-slate-400">
              Sequential · {DELAY_BETWEEN_CALLS_MS}ms between calls
            </p>
          </div>
        </div>

        {/* Rows */}
        <div className="max-h-[55vh] overflow-y-auto px-6 py-4">
          {rows.length === 0 ? (
            <div className="py-12 text-center font-sans text-sm text-slate-400">
              No feed posts found. Add a day with a post first.
            </div>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.frameId}
                  className={`flex items-center gap-3 p-3 border ${
                    r.status === 'generating'
                      ? 'border-[#C9A961] bg-[#C9A961]/5'
                      : r.status === 'done'
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : r.status === 'error'
                          ? 'border-red-200 bg-red-50/40'
                          : 'border-slate-200 bg-white'
                  } transition-colors`}
                >
                  {/* Thumb */}
                  <div className="w-10 h-10 flex-shrink-0 bg-slate-100 border border-slate-200 overflow-hidden">
                    {r.image ? (
                      <img src={r.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-slate-300" />
                      </div>
                    )}
                  </div>
                  {/* Day / theme */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-sm text-[#1A3B5C]">{r.dayLabel}</span>
                      <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400 truncate">
                        {r.theme || 'No theme'}
                      </span>
                    </div>
                    <div className="font-sans text-[10px] text-slate-500 mt-0.5 truncate">
                      {r.reason
                        ? r.reason
                        : r.result
                          ? `${r.result.caption.slice(0, 70)}${r.result.caption.length > 70 ? '…' : ''}`
                          : r.existingCaption
                            ? `${r.existingCaption.slice(0, 60)}…`
                            : 'No caption yet'}
                    </div>
                  </div>
                  {/* Status pill */}
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 border font-sans text-[10px] tracking-wider uppercase ${statusMeta[r.status].cls}`}
                  >
                    {statusMeta[r.status].icon}
                    {statusMeta[r.status].label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
          <p className="font-sans text-[11px] text-slate-500 max-w-md">
            We only touch Draft posts with an image and empty/short caption. Approved &amp; scheduled posts
            are left alone.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={running}
              className="px-4 py-2 border border-slate-300 text-slate-600 font-sans text-[10px] tracking-[0.2em] uppercase hover:border-slate-400 transition-colors disabled:opacity-40"
            >
              {finished ? 'Close' : 'Cancel'}
            </button>
            {running ? (
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-red-700 transition-colors"
              >
                <StopCircle className="w-3.5 h-3.5" /> Stop
              </button>
            ) : (
              <button
                onClick={runGenerate}
                disabled={eligibleCount === 0 || finished}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              >
                <PlayCircle className="w-3.5 h-3.5" />
                {finished
                  ? 'Run again'
                  : eligibleCount === 0
                    ? 'Nothing to generate'
                    : `Generate ${eligibleCount} post${eligibleCount === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; accent: string }> = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 px-3 py-2">
    <div className={`font-serif text-xl ${accent}`}>{value}</div>
    <div className="font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
  </div>
);

export default BulkAIGenerateModal;
